import { DefaultTheme } from 'styled-components'
import { breakpoints } from './mediaQueries'

const colorNames = {
    primary_700: '#DF6B00',
    primary_500: '#F6931E',
    primary_300: '#F9BB71',
    primary_100: '#FDF3E7',

    secondary_700: '#00166F',
    secondary_500: '#0C2487',
    secondary_300: '#5565BF',
    secondary_100: '#E8ECFD',

    grayscale_900_transparent: 'rgba(32, 24, 59, 0.64)',
    grayscale_900: '#20183B',
    grayscale_800: '#6F7095',
    grayscale_700: '#858DAD',
    grayscale_500: '#A9AFC9',
    grayscale_400: '#CED3E7',
    grayscale_300: '#EDEEF7',
    grayscale_200: '#F3F4F9',
    grayscale_100: '#F8F9FC',
    grayscale_0: '#FFFFFF',

    darkmode_900: '#0C0427',
    darkmode_500: '#33265E',

    blue: '#2A7DFF',
    blue_100: '#E6F0FF',
    blue_200: '#ACCFFF',
    blue_600: '#0C5FCC',
    wellness: '#BC955C',
    wellness_hover: '#855F2F',

    error: '#D00D1C',
    error_bg: '#FAE7E8',
    success: '#037F45',
    success_bg: '#ECF8F2',
    info: '#0F6A71',
    info_bg: '#E3F8FD',
    warning: '#FFBB00',
    warning_bg: '#FFF8E5',
    info_warning: '#858DAD',
    info_warning_bg: '#F3F4F9',
}

const fonts = {
    title: 'PlutoBold',
    text: 'Gotham Rounded Medium',
    text_bold: 'Gotham Rounded Bold',
    title_common: 'Ubuntu Bold',
    title_docs: 'PlutoBold',
    text_docs: 'Gotham Rounded Medium',
}

const theme: DefaultTheme = {
    mode: 'light',
    colors: {
        ...colorNames,
    },
    fonts: {
        ...fonts,
    },
    button: {
        primary: {
            bgColor: colorNames.primary_500,
            bgColorHover: colorNames.primary_700,
            bgColorActive: colorNames.grayscale_900,
            borderColor: colorNames.primary_500,
            borderColorHover: colorNames.primary_700,
            borderColorActive: colorNames.grayscale_900,
            contentColor: colorNames.grayscale_0,
            contentColorHover: colorNames.grayscale_0,
            contentColorActive: colorNames.grayscale_0,
            borderColorDisabled: colorNames.grayscale_400,
            contentColorDisabled: colorNames.grayscale_500,
        },
        secondary: {
            bgColor: colorNames.secondary_500,
            bgColorHover: colorNames.secondary_700,
            bgColorActive: colorNames.grayscale_900,
            borderColor: colorNames.secondary_500,
            borderColorHover: colorNames.secondary_700,
            borderColorActive: colorNames.grayscale_900,
            contentColor: colorNames.grayscale_0,
            contentColorHover: colorNames.grayscale_0,
            contentColorActive: colorNames.grayscale_0,
            borderColorDisabled: colorNames.grayscale_400,
            contentColorDisabled: colorNames.grayscale_500,
        },
        borderPrimary: {
            bgColor: 'transparent',
            bgColorHover: colorNames.primary_700,
            bgColorActive: colorNames.grayscale_900,
            borderColor: colorNames.primary_500,
            borderColorHover: colorNames.primary_700,
            borderColorActive: colorNames.grayscale_900,
            contentColor: colorNames.primary_500,
            contentColorHover: colorNames.grayscale_0,
            contentColorActive: colorNames.grayscale_0,
            borderColorDisabled: colorNames.grayscale_400,
            contentColorDisabled: colorNames.grayscale_500,
        },
        borderSecondary: {
            bgColor: 'transparent',
            bgColorHover: colorNames.secondary_700,
            bgColorActive: colorNames.grayscale_900,
            borderColor: colorNames.secondary_500,
            borderColorHover: colorNames.secondary_700,
            borderColorActive: colorNames.grayscale_900,
            contentColor: colorNames.secondary_500,
            contentColorHover: colorNames.grayscale_0,
            contentColorActive: colorNames.grayscale_0,
            borderColorDisabled: colorNames.grayscale_400,
            contentColorDisabled: colorNames.grayscale_500,
        },
    },
    linkVariants: {
        primary: {
            textColor: colorNames.primary_500,
            textColorHover: colorNames.primary_700,
            textColorActive: colorNames.grayscale_900,
            iconColor: colorNames.primary_500,
            iconColorHover: colorNames.primary_700,
            iconColorActive: colorNames.grayscale_900,
        },
        secondary: {
            textColor: colorNames.secondary_500,
            textColorHover: colorNames.secondary_700,
            textColorActive: colorNames.grayscale_900,
            iconColor: colorNames.primary_500,
            iconColorHover: colorNames.primary_700,
            iconColorActive: colorNames.grayscale_900,
        },
        gray: {
            textColor: colorNames.grayscale_800,
            textColorHover: colorNames.grayscale_500,
            textColorActive: colorNames.grayscale_900,
            iconColor: colorNames.grayscale_800,
            iconColorHover: colorNames.grayscale_500,
            iconColorActive: colorNames.grayscale_900,
        },
        error: {
            textColor: colorNames.error,
            textColorHover: colorNames.error,
            textColorActive: colorNames.error,
            iconColor: colorNames.error,
            iconColorHover: colorNames.error,
            iconColorActive: colorNames.error,
        },
        success: {
            textColor: colorNames.success,
            textColorHover: colorNames.success,
            textColorActive: colorNames.success,
            iconColor: colorNames.success,
            iconColorHover: colorNames.success,
            iconColorActive: colorNames.success,
        },
    },
    textVariants: {
        heading1: {
            fontSizeMobile: 30,
            lineHeightMobile: 38,
            fontSizeDesktop: 40,
            lineHeightDesktop: 50,
            fontFamily: fonts.title,
        },
        heading2: {
            fontSizeMobile: 24,
            lineHeightMobile: 30,
            fontSizeDesktop: 30,
            lineHeightDesktop: 38,
            fontFamily: fonts.title,
        },
        heading3: {
            fontSizeMobile: 18,
            lineHeightMobile: 24,
            fontSizeDesktop: 24,
            lineHeightDesktop: 30,
            fontFamily: fonts.title,
        },
        heading4: {
            fontSizeMobile: 16,
            lineHeightMobile: 22,
            fontSizeDesktop: 18,
            lineHeightDesktop: 24,
            fontFamily: fonts.title,
        },
        heading5: {
            fontSizeMobile: 14,
            lineHeightMobile: 20,
            fontSizeDesktop: 16,
            lineHeightDesktop: 22,
            fontFamily: fonts.title,
        },
        bodyLargeStrong: {
            fontSizeMobile: 16,
            lineHeightMobile: 22,
            fontSizeDesktop: 18,
            lineHeightDesktop: 24,
            fontFamily: fonts.text_bold,
        },
        bodyLarge: {
            fontSizeMobile: 16,
            lineHeightMobile: 22,
            fontSizeDesktop: 18,
            lineHeightDesktop: 24,
            fontFamily: fonts.text,
        },
        bodyStrong: {
            fontSizeMobile: 14,
            lineHeightMobile: 20,
            fontSizeDesktop: 16,
            lineHeightDesktop: 22,
            fontFamily: fonts.text_bold,
        },
        body: {
            fontSizeMobile: 14,
            lineHeightMobile: 20,
            fontSizeDesktop: 16,
            lineHeightDesktop: 22,
            fontFamily: fonts.text,
        },
        bodySmallStrong: {
            fontSizeMobile: 12,
            lineHeightMobile: 18,
            fontSizeDesktop: 14,
            lineHeightDesktop: 20,
            fontFamily: fonts.text_bold,
        },
        bodySmall: {
            fontSizeMobile: 12,
            lineHeightMobile: 18,
            fontSizeDesktop: 14,
            lineHeightDesktop: 20,
            fontFamily: fonts.text,
        },
        bodyExtraSmallStrong: {
            fontSizeMobile: 12,
            lineHeightMobile: 18,
            fontSizeDesktop: 12,
            lineHeightDesktop: 18,
            fontFamily: fonts.text_bold,
        },
        bodyExtraSmall: {
            fontSizeMobile: 12,
            lineHeightMobile: 18,
            fontSizeDesktop: 12,
            lineHeightDesktop: 18,
            fontFamily: fonts.text,
        },
        body16: {
            fontSizeMobile: 16,
            lineHeightMobile: 24,
            fontSizeDesktop: 16,
            lineHeightDesktop: 24,
            fontFamily: fonts.text,
        },
        body16Strong: {
            fontSizeMobile: 16,
            lineHeightMobile: 24,
            fontSizeDesktop: 16,
            lineHeightDesktop: 24,
            fontFamily: fonts.text_bold,
        },
        body14: {
            fontSizeMobile: 14,
            lineHeightMobile: 20,
            fontSizeDesktop: 14,
            lineHeightDesktop: 20,
            fontFamily: fonts.text,
        },
        body14Strong: {
            fontSizeMobile: 14,
            lineHeightMobile: 20,
            fontSizeDesktop: 14,
            lineHeightDesktop: 20,
            fontFamily: fonts.text_bold,
        },
        body12: {
            fontSizeMobile: 12,
            lineHeightMobile: 18,
            fontSizeDesktop: 12,
            lineHeightDesktop: 18,
            fontFamily: fonts.text,
        },
    },
}

const darkModeProperties: Pick<
    DefaultTheme,
    'mode' | 'colors' | 'linkVariants'
> = {
    mode: 'dark',
    colors: {
        ...colorNames,

        secondary_500: colorNames.grayscale_400,

        grayscale_900: colorNames.grayscale_400,
        grayscale_700: colorNames.grayscale_400,
        grayscale_200: colorNames.grayscale_900,
        grayscale_100: colorNames.grayscale_900,
        grayscale_0: colorNames.darkmode_900,
    },
    linkVariants: {
        primary: {
            ...theme.linkVariants.primary,
        },
        secondary: {
            ...theme.linkVariants.secondary,
            textColor: colorNames.grayscale_400,
            textColorHover: colorNames.grayscale_700,
        },
        gray: {
            ...theme.linkVariants.gray,
        },
        error: {
            ...theme.linkVariants.error,
        },
        success: {
            ...theme.linkVariants.success,
        },
    },
}

const darkMode: DefaultTheme = {
    ...theme,
    ...darkModeProperties,
}

const gridTheme = {
    breakpoints: {
        ...breakpoints,
    },
    container: {
        maxWidth: {
            xxl: 1200,
            xl: 1200,
            lg: 992,
            md: 768,
            sm: 576,
        },
        padding: 16,
    },
    col: {
        padding: 16,
    },
}

export { colorNames, darkMode, darkModeProperties, gridTheme, theme }
